import React, { useState, useEffect } from 'react';
import { TrendingUp, ThumbsUp, ThumbsDown, Activity } from 'lucide-react';
import { recommendationsAPI } from '../services/api';

function RLHFDashboard() {
  const [alignment, setAlignment] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAlignment();
  }, []);

  const loadAlignment = async () => {
    try {
      setLoading(true);
      const response = await recommendationsAPI.getAlignment();
      setAlignment(response.data);
    } catch (error) {
      console.error('Error loading alignment:', error);
      // Set demo data
      setAlignment({
        alignment_score: 0.73,
        total_feedback: 15,
        accepts: 11,
        rejects: 4
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="card">
        <div className="loading">
          <div className="spinner"></div>
        </div>
      </div>
    );
  }

  const acceptanceRate = alignment?.accepts && alignment?.total_feedback
    ? (alignment.accepts / alignment.total_feedback) * 100
    : 0;

  return (
    <div className="card">
      <div className="card-header">
        <h3 className="card-title">RLHF Alignment Dashboard</h3>
        <p className="card-subtitle">Recommendation acceptance tracking and model improvement</p>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-4" style={{ gap: '16px', marginTop: '16px', marginBottom: '24px' }}>
        <div style={{ padding: '16px', background: 'var(--bg-secondary)', borderRadius: '8px', textAlign: 'center' }}>
          <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '8px' }}>Alignment Score</p>
          <p style={{ fontSize: '2rem', fontWeight: '700', color: 'var(--primary-600)' }}>
            {((alignment?.alignment_score || 0) * 100).toFixed(0)}%
          </p>
        </div>
        <div style={{ padding: '16px', background: 'var(--bg-secondary)', borderRadius: '8px', textAlign: 'center' }}>
          <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '8px' }}>Total Feedback</p>
          <p style={{ fontSize: '2rem', fontWeight: '700' }}>
            {alignment?.total_feedback || 0}
          </p>
        </div>
        <div style={{ padding: '16px', background: 'hsl(142, 76%, 96%)', borderRadius: '8px', textAlign: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginBottom: '8px' }}>
            <ThumbsUp size={16} style={{ color: 'var(--success-600)' }} />
            <p style={{ fontSize: '0.75rem', color: 'var(--success-600)' }}>Accepts</p>
          </div>
          <p style={{ fontSize: '2rem', fontWeight: '700', color: 'var(--success-600)' }}>
            {alignment?.accepts || 0}
          </p>
        </div>
        <div style={{ padding: '16px', background: 'hsl(0, 86%, 97%)', borderRadius: '8px', textAlign: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginBottom: '8px' }}>
            <ThumbsDown size={16} style={{ color: 'var(--error-600)' }} />
            <p style={{ fontSize: '0.75rem', color: 'var(--error-600)' }}>Rejects</p>
          </div>
          <p style={{ fontSize: '2rem', fontWeight: '700', color: 'var(--error-600)' }}>
            {alignment?.rejects || 0}
          </p>
        </div>
      </div>

      {/* Acceptance Rate Visualization */}
      <div style={{ padding: '20px', background: 'var(--bg-tertiary)', borderRadius: '12px', marginBottom: '24px' }}>
        <p style={{ fontSize: '0.875rem', fontWeight: '600', marginBottom: '12px' }}>Recommendation Acceptance Rate</p>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <div style={{ 
            flex: 1, 
            height: '24px', 
            background: 'var(--bg-secondary)', 
            borderRadius: '12px',
            overflow: 'hidden'
          }}>
            <div style={{ 
              width: `${acceptanceRate}%`, 
              height: '100%', 
              background: 'linear-gradient(90deg, var(--success-500), var(--success-600))',
              transition: 'width 0.5s ease'
            }}></div>
          </div>
          <span style={{ fontSize: '1.5rem', fontWeight: '700', color: 'var(--success-600)' }}>
            {acceptanceRate.toFixed(0)}%
          </span>
        </div>
      </div>

      {/* Insights */}
      <div>
        <h4 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Activity size={20} />
          Model Performance Insights
        </h4>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {acceptanceRate >= 70 ? (
            <div style={{ padding: '12px', background: 'hsl(142, 76%, 96%)', border: '1px solid hsl(142, 56%, 85%)', borderRadius: '8px' }}>
              <p style={{ fontSize: '0.875rem', fontWeight: '600', color: 'var(--success-700)' }}>✓ High Alignment</p>
              <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginTop: '4px' }}>
                AI recommendations are well-aligned with user decisions. Model is performing excellently.
              </p>
            </div>
          ) : (
            <div style={{ padding: '12px', background: 'hsl(38, 92%, 95%)', border: '1px solid hsl(38, 72%, 85%)', borderRadius: '8px' }}>
              <p style={{ fontSize: '0.875rem', fontWeight: '600', color: 'var(--warning-700)' }}>⚠ Moderate Alignment</p>
              <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginTop: '4px' }}>
                Consider retraining the reward model with recent feedback to improve alignment.
              </p>
            </div>
          )}
          
          {(alignment?.total_feedback || 0) >= 10 && (
            <div style={{ padding: '12px', background: 'hsl(199, 89%, 96%)', border: '1px solid hsl(199, 69%, 85%)', borderRadius: '8px' }}>
              <p style={{ fontSize: '0.875rem', fontWeight: '600', color: 'var(--info-700)' }}>💡 Ready for Retraining</p>
              <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginTop: '4px' }}>
                Sufficient feedback collected ({alignment.total_feedback} samples). Reward model can be retrained for improved predictions.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default RLHFDashboard;
